import type { Hit } from "instantsearch.js";
import { Suspense } from "react";
import { Await, LoaderFunction, redirect, useLoaderData, useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { useMemberPricingContext } from "@/hooks/useMemberPricingContext";
import { useRetailMemberPrices } from "@/hooks/useRetailMemberPrices";
import { shopDiscountsQuery, useShopDiscounts } from "@/hooks/useShopDiscounts";
import {
  type CatalogCardContext,
  type CatalogHit,
  catalogHitListingType,
  isRetailHit,
  isUuid,
  toCatalogCard,
} from "@/utils/algoliaCatalog";
import {
  getAdonisTopRecommendationsId,
  getGiftCards,
  getMemberships,
  getPlatformCurrent,
  getPromotions,
  getRetailproductsAreRetailProductsVisible,
  getShop,
  getShopCustomCategoriesByCategoryIdListings,
  getSkindropIsVisibleOnMarketplace,
  getUser,
} from "api/methods";
import { Models } from "api/types";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { fetchCatalogLocationId } from "hooks/useCatalogLocationId";
import { ProductCardLarge } from "molecules/cards/product-card-large/ProductCardLarge";
import { queryClient } from "pages/Root";
import { NextFilterAndSortListings } from "pages/root/shop/NextFilterAndSortListings";
import {
  getDiscountCustomColor,
  getDiscountText,
  getFreeGiftPackageIds,
} from "services/ListingHelperService";
import { waitObj } from "toolbox/Promise";

import { CatalogMembershipCard } from "./CatalogMembershipCard";
import { ShopListHeader } from "./ShopListHeader/ShopListHeader";
import { ShopListingSkeleton } from "./ShopListingSkeleton";

type TranslateFn = ReturnType<typeof useTranslation>["t"];

const getGiftCardPriceRangeString = (
  giftCard: Models<"DisplayGiftCardListing">,
  t: TranslateFn,
) => {
  if (giftCard.giftCards.length === 0) {
    return t("shopCustomCategory.priceZero");
  }

  const costs = giftCard.giftCards.map((gc) => gc.baseCostInCents);
  const minCost = Math.min(...costs);
  const maxCost = Math.max(...costs);

  if (minCost === maxCost) {
    return t("shopCustomCategory.priceFrom", { amount: minCost / 100 });
  }

  return t("shopCustomCategory.priceRange", {
    minAmount: minCost / 100,
    maxAmount: maxCost / 100,
  });
};

const DEFAULT_GIFT_CARD_IMAGE = "https://assets.dev.repeatmd.app/giftcard-justforyou.webp";

const loader = async ({ categoryId }: { categoryId: string }) => {
  const shopData = queryClient.fetchQuery({ ...getShop.keyed() });
  const customCategoryListings = queryClient.fetchQuery({
    ...getShopCustomCategoriesByCategoryIdListings.keyed({ categoryId }),
    staleTime: 1_000,
  });
  // Badges fill in when this lands; the page does not wait for it.
  void queryClient.prefetchQuery(shopDiscountsQuery());
  const user = queryClient.fetchQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });
  // Memberships stay on REST: the card needs their billing-period fields, which the index does not
  // carry. The index still decides which memberships the category holds and in what order.
  const memberships = queryClient.fetchQuery({ ...getMemberships.keyed() });

  const platform = await queryClient.fetchQuery({ ...getPlatformCurrent.keyed() });

  const giftCardsEnabled = platform?.platformSettings?.giftCardEnabled;

  const giftCards = giftCardsEnabled
    ? await queryClient.fetchQuery({ ...getGiftCards.keyed({}) })
    : [];

  const recommendationIds = queryClient.fetchQuery({
    ...getAdonisTopRecommendationsId.keyed({ limit: 10 }),
    staleTime: 1000 * 30,
  });

  const areRetailProductsVisible = queryClient.fetchQuery({
    ...getRetailproductsAreRetailProductsVisible.keyed(),
    staleTime: 1000 * 10,
  });

  // Skindrop visibility is non-critical; if the endpoint fails, default to false
  const isSkinDropVisible = queryClient
    .fetchQuery({
      ...getSkindropIsVisibleOnMarketplace.keyed(),
      staleTime: 1000 * 10,
      retry: false,
    })
    .catch(() => false);

  const platformLocationId = user.then((userData) => fetchCatalogLocationId(queryClient, userData));

  const promotions = queryClient.fetchQuery({
    ...getPromotions.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  return waitObj({
    categoryId,
    platform,
    customCategoryListings,
    shopData,
    user,
    platformLocationId,
    memberships,
    giftCards,
    recommendationIds,
    areRetailProductsVisible,
    isSkinDropVisible,
    promotions,
  });
};

type LoaderData = Awaited<ReturnType<typeof loader>>;
export const shopCustomCategoryLoader: LoaderFunction = ({ params: { id: categoryId } }) => {
  if (!categoryId) {
    return redirect("/home");
  }
  // The id is interpolated into the index filter; anything but a UUID is not a category.
  if (!isUuid(categoryId)) {
    return redirect("/shop");
  }
  return { data: loader({ categoryId }) };
};

/** A retail card asks for retail member prices itself, so a category requests them only once it renders retail. */
export const CatalogHitCard = ({
  hit,
  cardContext,
  onClick,
  guestMode,
  isRecommended,
}: {
  hit: Hit<CatalogHit>;
  cardContext: CatalogCardContext;
  onClick: () => void;
  guestMode: boolean | undefined;
  isRecommended: boolean;
}) => {
  const retailMemberPrices = useRetailMemberPrices(isRetailHit(hit));
  const card = toCatalogCard(hit, { ...cardContext, retailMemberPrices });

  return (
    <ProductCardLarge
      itemId={card.id}
      title={card.title}
      description={card.description}
      imageUrl={card.imageUrl}
      type={card.type}
      tag=""
      concerns={card.concerns}
      price={card.price}
      memberPrice={card.memberPrice}
      onClick={onClick}
      hasLinearPricing={card.hasLinearPricing}
      unitType={card.unitType}
      discount={card.discountText}
      discountData={card.discount}
      customColor={card.customColor}
      guestMode={guestMode}
      isRecommended={isRecommended}
    />
  );
};

const ShopCustomCategoryContent = ({ data }: { data: LoaderData }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    categoryId,
    platform,
    customCategoryListings,
    shopData,
    user,
    platformLocationId,
    memberships,
    recommendationIds,
    giftCards,
    areRetailProductsVisible,
    isSkinDropVisible,
    promotions,
  } = data;
  const shopDiscounts = useShopDiscounts();
  const memberPricing = useMemberPricingContext();

  const cardContext = {
    shopDiscounts,
    customShopCategories: [customCategoryListings],
    freeGiftPackageIds: getFreeGiftPackageIds(promotions),
    memberPricing,
    retailMemberPrices: undefined,
  };
  const recommendedIds = new Set(recommendationIds);
  const membershipsById = new Map(memberships.map((membership) => [membership.id, membership]));
  const giftCardsById = new Map(giftCards.map((giftCard) => [giftCard.id, giftCard]));

  return (
    <div className="flex flex-col bg-two">
      <ShopCustomCategoryPageHeader category={customCategoryListings.category} />
      {customCategoryListings.categoryInclusions.length > 0 && (
        <NextFilterAndSortListings
          surface="category"
          types={["Treatment", "Package", "Membership", "Product", "GiftCard"]}
          areas={shopData.areas ?? []}
          concerns={shopData.concerns ?? []}
          categoryId={categoryId}
          hitsPerPage={customCategoryListings.categoryInclusions.length}
          platformId={platform.id}
          platformLocationId={platformLocationId}
          visibility={{ areRetailProductsVisible, isSkinDropVisible }}
          shopDiscounts={shopDiscounts}
          hitFilter={(hit) => {
            switch (catalogHitListingType(hit)) {
              case "Membership":
                return membershipsById.has(hit.objectID);
              case "GiftCard":
                return giftCardsById.has(hit.objectID);
              default:
                return true;
            }
          }}
        >
          {(hit) => {
            const card = toCatalogCard(hit, cardContext);
            const open = () => navigate(card.pdpPath);

            if (card.type === "Membership") {
              return (
                <CatalogMembershipCard
                  membership={membershipsById.get(hit.objectID)!}
                  shopDiscounts={shopDiscounts}
                  customShopCategories={[customCategoryListings]}
                  onClick={open}
                  guestMode={user?.isGuest}
                  isRecommended={recommendedIds.has(hit.objectID)}
                />
              );
            }

            if (card.type === "GiftCard") {
              const giftCard = giftCardsById.get(hit.objectID)!;
              const rawDiscount = shopDiscounts?.listingDiscounts[giftCard.id];
              const discount = rawDiscount?.isSignUpReward ? undefined : rawDiscount;

              return (
                <ProductCardLarge
                  itemId={giftCard.id}
                  title={t("shopCustomCategory.giftCardTitle")}
                  description={t("shopCustomCategory.giftCardDescription")}
                  imageUrl={DEFAULT_GIFT_CARD_IMAGE}
                  type="GiftCard"
                  tag=""
                  concerns={[]}
                  price={0}
                  onClick={open}
                  discount={discount ? getDiscountText(discount) : ""}
                  discountData={discount}
                  customColor={getDiscountCustomColor(discount, [customCategoryListings])}
                  guestMode={user?.isGuest}
                  priceRange={getGiftCardPriceRangeString(giftCard, t)}
                />
              );
            }

            return (
              <CatalogHitCard
                hit={hit}
                cardContext={cardContext}
                onClick={open}
                guestMode={user?.isGuest}
                isRecommended={recommendedIds.has(card.id)}
              />
            );
          }}
        </NextFilterAndSortListings>
      )}
      <RepeatMDFooter />
    </div>
  );
};

const ShopCustomCategoryPage = () => {
  const { data } = useLoaderData() as { data: Promise<LoaderData> };
  return (
    <Suspense fallback={<ShopListingSkeleton />}>
      <Await resolve={data}>{(resolved) => <ShopCustomCategoryContent data={resolved} />}</Await>
    </Suspense>
  );
};

const ShopCustomCategoryPageHeader = ({ category }: { category: Models<"CategoryModel"> }) => (
  <ShopListHeader
    title={category.title ?? ""}
    subtitle={category.description ?? ""}
    backgroundImageUrl={category.headerImageUrl ?? ""}
    bgGradient
  />
);

export default ShopCustomCategoryPage;
